import { rfidLocation } from './rfidLocation';
import RfidQuickLogin from './RfidQuickLogin';
import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axiosConfig";
import { AuthContext } from "../AuthContext";
import { v4 as uuidv4 } from "uuid";
import "../assets/CSS/login.css";
import loginLogo from "../assets/Img/loginLogo.png";

const getPostLoginRoute = (userData) => {
  const role = String(userData?.Auth || "").trim().toLowerCase();
  const department = String(userData?.Department || "").trim().toLowerCase();

  // Accounts users belong to the operational dashboard, not the worker app.
  // Department fallback protects legacy accounts that were incorrectly saved as Auth=User.
  if (role === "accounts" || department.includes("account")) {
    return "/Main-dashboard";
  }

  const routes = {
    user: "/User-dashboard",
    admin: "/Main-dashboard",
    supervisor: "/Main-dashboard",
    superadmin: "/Main-dashboard",
    staff: "/staff-dashboard",
  };

  return routes[role] || "/";
};

function Login() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const [formData, setFormData] = useState({ identifier: "", Password: "" });
  const [error, setError] = useState("");
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState('password');
  const [rfid, setRfid] = useState('');
  const submitting = useRef(false);
  const rfidInput = useRef(null);

  useEffect(() => {
    if (loginMode === 'rfid') rfidInput.current?.focus();
  }, [loginMode]);

  // Auto Redirect if logged in
  useEffect(() => {
    try {
      const loginTime = localStorage.getItem("loginTime");
      const userDataRaw = localStorage.getItem("userData");

      if (!loginTime || !userDataRaw) return;

      const userData = JSON.parse(userDataRaw);

      const now = new Date().getTime();
      const login = new Date(loginTime).getTime();

      const diffMinutes = (now - login) / (1000 * 60);

      if (diffMinutes > 15) {
        localStorage.removeItem('user');
        localStorage.removeItem('loginTime');
        localStorage.removeItem('sessionExpiration');
        localStorage.removeItem('userData');
        return;
      }

      navigate(getPostLoginRoute(userData));

    } catch (error) {
      localStorage.removeItem('user');
      localStorage.removeItem('loginTime');
      localStorage.removeItem('sessionExpiration');
      localStorage.removeItem('userData');
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleKeyDown = (e) => {
    setCapsLockOn(e.getModifierState("CapsLock"));
  };

  const getISTTime = () => {
    const now = new Date();
    const offset = 5.5 * 60 * 60 * 1000;
    const istTime = new Date(now.getTime() + offset);
    return istTime.toISOString().replace("T", " ").slice(0, 19);
  };

  /*const getLocation = async () => {
    try {
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();
      return `${data.city}, ${data.country_name}`;
    } catch {
      return "Unknown";
    }
  };*/

  const getClientInfo = async () => {
    try {
      // Step 1: Get IPv4
      const ipRes = await fetch("https://api.ipify.org?format=json");
      const ipData = await ipRes.json();

      // Step 2: Pass IPv4 to ipapi
      const locRes = await fetch(`https://ipapi.co/json/`);
      const locData = await locRes.json();

      return {
        ip: ipData.ip,
        location: `${locData.city}, ${locData.region}, ${locData.country_name}`,
        latitude: locData.latitude,
        longitude: locData.longitude,
      };
    } catch {
      return {
        ip: "Unknown",
        location: "Unknown",
        latitude: null,
        longitude: null,
      };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setError("");
    setLoading(true);

    try {
      //const deviceToken = uuidv4();
      let deviceToken = localStorage.getItem("deviceToken");

      if (!deviceToken) {
        deviceToken = uuidv4();
        localStorage.setItem("deviceToken", deviceToken);
      }
      //const location = await getLocation();
      const clientInfo = loginMode === 'rfid'
        ? { location: 'RFID terminal', ip: undefined, ...await rfidLocation() }
        : await getClientInfo();

      const response = await axiosInstance.post(loginMode === 'rfid' ? "/api/rfid-login" : "/api/login", {
        ...(loginMode === 'rfid'
          ? { RFID: rfid.trim() }
          : { identifier: formData.identifier, Password: formData.Password }),
        deviceToken,
        deviceName: navigator.platform,
        browser: navigator.userAgent,
        dashboardType: "WIP",
        loginLocation: clientInfo.location,
        clientIP: clientInfo.ip,
        latitude: clientInfo.latitude,
        longitude: clientInfo.longitude,
        ...(clientInfo.locationError && { locationError: clientInfo.locationError }),
      });
      
      localStorage.removeItem('user');
      localStorage.removeItem('loginTime');
      localStorage.removeItem('sessionExpiration');
      localStorage.removeItem('userData');

      if (loginMode === 'rfid') {
        if (response.data.attendance?.message) sessionStorage.setItem('rfid-attendance-message', response.data.attendance.message);
        for (const key of Object.keys(sessionStorage)) {
          if (key.startsWith('worker-bundle-draft-v1:')) sessionStorage.removeItem(key);
        }
      }
      const userData = response.data.user;
      const loginTime = getISTTime();
      localStorage.setItem("loginTime", loginTime);
      localStorage.setItem("deviceToken", deviceToken);
      localStorage.setItem("userData", JSON.stringify(userData));
      login(userData);

      showAlert("Login successful! Redirecting...", "success");

      setTimeout(() => {
        navigate(getPostLoginRoute(userData));
      }, 1500);
    } catch (err) {
      const res = err.response?.data;

      if (res?.status === "DEVICE_PENDING") {
        setError(
          "New device detected. Your device has been registered and is awaiting administrator approval."
        );
      } else if (res?.status === "DEVICE_NOT_APPROVED") {
        setError(
          "Your device is awaiting administrator approval. Please contact IT/Admin."
        );
      } else {
        setError(loginMode === 'rfid' && err.response?.status === 401
          ? 'Card not recognised or worker access is disabled.'
          : (res?.error || "Login failed"));
      }
      if (loginMode === 'rfid') {
        setRfid('');
        rfidInput.current?.focus();
      }
      submitting.current = false;
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    const alertPlaceholder = document.getElementById("alertPlaceholder");
    alertPlaceholder.innerHTML = `
      <div class="alert alert-${type}">
        ${message}
      </div>
    `;
    setTimeout(() => (alertPlaceholder.innerHTML = ""), 2000);
  };

  const handleRedirect = () => {
    navigate("/wip-login");
  };

  return (
    <div className="login-wrapper">
      <RfidQuickLogin />
      <button className="btn btn-dark btn-sm rm-btn" onClick={handleRedirect}>
        RM →
      </button>

      <div className="login-card">
        <div id="alertPlaceholder"></div>

        <img src={loginLogo} className="login-logo" alt="Logo" />

        {error && <div className="alert alert-danger">{error}</div>}

        <h1 className="login-heading">{loginMode === 'rfid' ? 'Scan your card' : 'Welcome back'}</h1>
        <form onSubmit={handleSubmit}>
          {loginMode === 'rfid' ? <>
            <p className="login-hint">Tap your RFID card on the reader.</p>
            <input ref={rfidInput} type="password" name="RFID" aria-label="RFID card number"
              placeholder="Scan RFID card" value={rfid} autoFocus autoComplete="off"
              maxLength={64} inputMode="numeric" pattern="[0-9]{6,64}"
              onChange={e => setRfid(e.target.value)} required readOnly={loading} />
          </> : <>
          <input
            type="text"
            name="identifier"
            placeholder="Employee ID"
            value={formData.identifier}
            onChange={handleChange}
            required
          />

          <input
            type="password"
            name="Password"
            placeholder="Password"
            value={formData.Password}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            required
          />

          {capsLockOn && <p className="caps-warning"> Caps Lock is ON</p>}
          </>}

          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
        <div className="login-alternative">
          <button type="button" className="login-alternative-btn" disabled={loading}
            onClick={() => {
              setLoginMode(loginMode === 'rfid' ? 'password' : 'rfid');
              setError('');
              setRfid('');
            }}>
            {loginMode === 'rfid' ? 'Back to login' : 'Login with RFID card'}
          </button>
        </div>
      </div>

      {/* Floating shapes */}
      <div className="bg-circle circle1"></div>
      <div className="bg-circle circle2"></div>
    </div>
  );
}

export default Login;
